import { useCallback, useMemo } from "react";
import type { FocusEvent, ReactNode } from "react";
import { TextInput } from "@mantine/core";

import {
  getFragmentCountDisplayValue,
  normalizeFragmentChipValue,
  type FragmentCountPatch,
} from "../../../utils/fragmentCount";

const INVALID_FRAGMENT_MESSAGE =
  "Enter a whole number from 1 to 999, type Multiple, or leave blank.";

export interface AdminFragmentCountParseResult {
  displayValue: string;
  patch: FragmentCountPatch | null;
  error: string | null;
}

export const parseAdminFragmentCountDraft = (
  draft: string
): AdminFragmentCountParseResult => {
  const patch = normalizeFragmentChipValue(draft, null, null);
  if (!patch)
    return {
      displayValue: draft,
      patch: null,
      error: INVALID_FRAGMENT_MESSAGE,
    };
  return {
    displayValue: getFragmentCountDisplayValue(
      patch.fragmentCount,
      patch.fragmentCountQualifier
    ),
    patch,
    error: null,
  };
};

interface AdminFragmentCountInputProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  error?: ReactNode;
}

export default function AdminFragmentCountInput({
  value,
  onChange,
  onBlur,
  error,
}: AdminFragmentCountInputProps) {
  const parsed = useMemo(() => parseAdminFragmentCountDraft(value), [value]);
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) =>
      onChange(event.currentTarget.value),
    [onChange]
  );
  const handleBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      if (parsed.patch) onChange(parsed.displayValue);
      onBlur?.(event);
    },
    [onBlur, onChange, parsed]
  );

  return (
    <TextInput
      label="Fragment Count"
      description="Enter a whole number from 1 to 999, or type Multiple"
      placeholder="e.g., 1, 2, 3, or Multiple"
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
      error={error ?? parsed.error}
    />
  );
}
